import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Card, Button, Alert, Form, Spinner, ListGroup, Image, Badge } from 'react-bootstrap';
import { Castaway } from '../types';
import TribeBadge, { getTribeColor } from './TribeBadge';
import CastawayBioModal from './CastawayBioModal';
import '../App.css';

interface UserInfo {
  userName: string;
  email: string;
  survivorName?: string;
  realName?: string;
  leagueId: string;
  paymentMethod: string;
}

const EliminationPick: React.FC = () => {
  const { leagueId } = useParams<{ leagueId: string }>();
  const [castaways, setCastaways] = useState<Castaway[]>([]);
  const [eliminationPick, setEliminationPick] = useState<number | null>(null);
  const [currentEpisode, setCurrentEpisode] = useState<string>('2');
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [votedOutCastaways, setVotedOutCastaways] = useState<Set<number>>(new Set());
  const [deadline, setDeadline] = useState<string | null>(null);
  const [existingPick, setExistingPick] = useState<number | null>(null);
  const [tribes, setTribes] = useState<{ name: string; color: string }[]>([]);
  const [submittingCastawayId, setSubmittingCastawayId] = useState<number | null>(null);
  const [selectedBioCastaway, setSelectedBioCastaway] = useState<Castaway | null>(null);
  const [selectedSeason, setSelectedSeason] = useState<number>(() => parseInt(localStorage.getItem('selectedSeason') || '49'));

  const loadUserInfo = useCallback(() => {
    const currentSeason = parseInt(localStorage.getItem('selectedSeason') || '49');
    const userKey = `fantasyUser_${currentSeason}`;
    const storedUser = localStorage.getItem(userKey);
    if (storedUser) {
      setUserInfo(JSON.parse(storedUser));
    } else {
      setUserInfo(null);
    }
  }, []);

  useEffect(() => {
    loadUserInfo();

    const handleSeasonChange = () => {
      setSelectedSeason(parseInt(localStorage.getItem('selectedSeason') || '49'));
      loadUserInfo();
    };
    window.addEventListener('seasonChanged', handleSeasonChange);
    return () => window.removeEventListener('seasonChanged', handleSeasonChange);
  }, [loadUserInfo]);

  useEffect(() => {
    const fetchData = async () => {
      if (!leagueId) return;
      setIsLoading(true);
      try {
        // Fetch league details to get cutoff dates
        const leagueResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/all`);
        const leagueData = await leagueResponse.json();
        const currentLeague = leagueData.leagues.find((l: any) => l.id === leagueId);

        let leagueSeason = selectedSeason;
        if (currentLeague && currentLeague.season && currentLeague.season !== selectedSeason) {
            leagueSeason = currentLeague.season;
            setSelectedSeason(leagueSeason);
        }

        // Robust user identification: Find the member entry in THIS league that matches the primary user's email.
        // This ensures we use the correct Survivor Name (userName) for this specific season/league.
        let currentUser = userInfo;
        const primaryUserStr = localStorage.getItem('fantasyUser');
        
        if (primaryUserStr && currentLeague) {
          const primaryUser = JSON.parse(primaryUserStr);
          if (primaryUser.email) {
            const matchingMember = currentLeague.members?.find((m: any) => m.email === primaryUser.email);
            if (matchingMember) {
              const updatedUser: UserInfo = {
                userName: matchingMember.userName,
                email: matchingMember.email,
                leagueId: leagueId as string,
                paymentMethod: matchingMember.paymentMethod || ''
              };
              currentUser = updatedUser;
              
              if (!userInfo || userInfo.userName !== updatedUser.userName || userInfo.leagueId !== leagueId) {
                setUserInfo(updatedUser);
                localStorage.setItem(`fantasyUser_${leagueSeason}`, JSON.stringify(updatedUser));
              }
            }
          }
        }

        if (currentLeague && currentLeague.weeklyEliminationCutoffs) {
          const cutoffs = currentLeague.weeklyEliminationCutoffs;
          const now = Date.now();
          const nextOpenEpisode = Object.entries(cutoffs)
            .filter(([, cutTime]) => new Date(cutTime as string).getTime() > now)
            .sort(([a], [b]) => Number(a) - Number(b))[0];
          
          if (nextOpenEpisode && currentEpisode === '2' && !sessionStorage.getItem('eliminationPickUserSelectedEp')) {
            setCurrentEpisode(nextOpenEpisode[0]);
          }
          setDeadline(currentLeague.weeklyEliminationCutoffs[currentEpisode]);
        }
        if (currentLeague && currentLeague.tribes) {
          setTribes(currentLeague.tribes);
        }

        // Fetch existing picks
        if (currentUser) {
          const picksResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${leagueId}/elimination-picks`);
          const picksData = await picksResponse.json();
          const userPicks = picksData.eliminationPicks[currentUser.userName];
          if (userPicks && userPicks[currentEpisode]) {
            setExistingPick(userPicks[currentEpisode]);
            setEliminationPick(userPicks[currentEpisode]);
          }
        }

        // Fetch castaways
        const castawaysResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${leagueId}/castaways`);
        const castawaysData = await castawaysResponse.json();
        setCastaways(castawaysData.castaways);

        // Fetch scores to determine who is voted out
        const scoresResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/scores/${selectedSeason}`);
        const scoresData = await scoresResponse.json();
        const votedOutIds = new Set<number>();
        Object.values(scoresData.scores || {}).forEach((episodeScores: any) => {
          Object.entries(episodeScores).forEach(([castawayId, score]: [string, any]) => {
            if (score.votedOut || score.involuntaryEvacuation || score.voluntaryExit) {
              votedOutIds.add(Number(castawayId));
            }
          });
        });
        setVotedOutCastaways(votedOutIds);

      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load data.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [leagueId, currentEpisode, selectedSeason, userInfo?.userName, userInfo?.leagueId]);

  const handleDirectPick = async (castawayId: number) => {
    if (isFormDisabled) return;
    if (!userInfo) {
      setError('You must be logged in to make a pick.');
      return;
    }

    setSubmittingCastawayId(castawayId);
    setError(null);
    setSuccess(null);

    const castaway = castaways.find(c => c.id === castawayId);
    const castawayName = castaway ? castaway.name : 'Castaway';

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${leagueId}/elimination-pick`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userName: userInfo.userName,
          episode: currentEpisode,
          castawayId: castawayId,
        }),
      });

      if (response.ok) {
        setSuccess(`🎯 Picked ${castawayName} to be voted out for Episode ${currentEpisode}!`);
        setExistingPick(castawayId);
        setEliminationPick(castawayId);
      } else {
        const errorData = await response.json();
        setError(`Failed to submit elimination pick: ${errorData.message}`);
      }
    } catch (err: any) {
      console.error('Error submitting elimination pick:', err);
      setError('An error occurred while submitting your pick.');
    } finally {
      setSubmittingCastawayId(null);
    }
  };

  const activeCastaways = castaways.filter(c => !votedOutCastaways.has(c.id));
  const isDeadlinePassed = deadline ? new Date() > new Date(deadline) : false;
  const isFormDisabled = isDeadlinePassed || submittingCastawayId !== null;

  const currentPickCastaway = castaways.find(c => c.id === existingPick);

  return (
    <Container className="mt-5 mb-5">
      <Card className="shadow-sm home-card">
        <Card.Header as="h4" className="card-title-custom text-center">
          Season {selectedSeason}: Weekly Elimination Pick
        </Card.Header>
        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">Select Episode:</Form.Label>
            <div className="d-flex flex-wrap gap-2">
              {Array.from({ length: 12 }, (_, i) => i + 2).map(episodeNum => (
                <Button
                  key={episodeNum}
                  variant={currentEpisode === String(episodeNum) ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => {
                    sessionStorage.setItem('eliminationPickUserSelectedEp', 'true');
                    setCurrentEpisode(String(episodeNum));
                    setSuccess(null);
                    setError(null);
                  }}
                  className="px-3 py-1"
                >
                  Episode {episodeNum}
                </Button>
              ))}
            </div>
          </Form.Group>

          {isLoading && <div className="text-center py-4"><Spinner animation="border" /></div>}
          {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
          {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}
          {deadline && (
            <Alert variant="secondary" className="d-flex align-items-center justify-content-between flex-wrap gap-2 py-2">
              <span><strong>Deadline for Episode {currentEpisode}:</strong> {new Date(deadline).toLocaleString()}</span>
              {isDeadlinePassed ? <Badge bg="danger">Deadline Passed</Badge> : <Badge bg="success">Picks Open</Badge>}
            </Alert>
          )}
          {isDeadlinePassed && <Alert variant="warning">The deadline for Episode {currentEpisode} has passed. Picks are locked.</Alert>}
          
          {existingPick && (
            <Alert variant="info" className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div>
                <strong>Your Current Pick for Episode {currentEpisode}: </strong>
                <span className="fw-bold text-primary">{currentPickCastaway?.name || `ID #${existingPick}`}</span>
              </div>
              {!isDeadlinePassed && (
                <span className="small text-muted">Click any "Pick" button below to switch your pick before the deadline.</span>
              )}
            </Alert>
          )}
          
          {!isLoading && !error && (
            <Card className="border shadow-sm mt-3">
              <Card.Header as="h5" className="d-flex justify-content-between align-items-center bg-light">
                <span>Active Castaways ({activeCastaways.length} remaining)</span>
                <small className="text-muted fw-normal" style={{ fontSize: '0.82rem' }}>
                  Click <strong>Pick to Eliminate</strong> directly on a contestant
                </small>
              </Card.Header>
              <ListGroup variant="flush">
                {activeCastaways.map((castaway) => {
                  const isSelected = existingPick === castaway.id;
                  const isSubmittingThis = submittingCastawayId === castaway.id;
                  const tribeColor = getTribeColor(castaway.tribe, tribes, castaway.tribeColor);

                  return (
                    <ListGroup.Item
                      key={castaway.id}
                      className={`d-flex justify-content-between align-items-center p-3 ${isSelected ? 'bg-primary-subtle border-start border-primary border-4' : ''}`}
                    >
                      <div
                        className="d-flex align-items-center cursor-pointer"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedBioCastaway(castaway)}
                        title="Click to view contestant bio"
                      >
                        <Image
                          src={castaway.imageUrl}
                          roundedCircle
                          referrerPolicy="no-referrer"
                          style={{
                            width: '52px',
                            height: '52px',
                            marginRight: '15px',
                            objectFit: 'cover',
                            border: `3px solid ${tribeColor}`,
                            boxShadow: isSelected ? '0 0 8px rgba(13, 110, 253, 0.5)' : 'none'
                          }}
                        />
                        <div>
                          <strong className={isSelected ? 'text-primary fs-5' : 'fs-6'}>
                            {castaway.name}
                          </strong>{' '}
                          <span className="text-muted small">({castaway.age})</span>
                          {isSelected && (
                            <Badge bg="primary" className="ms-2">
                              ✓ Your Pick
                            </Badge>
                          )}
                          <div className="d-flex align-items-center gap-1 mt-1">
                            <TribeBadge tribeName={castaway.tribe} tribes={tribes} tribeColor={castaway.tribeColor} />
                            <small className="text-muted">{castaway.occupation}</small>
                          </div>
                        </div>
                      </div>

                      <div className="d-flex align-items-center gap-2">
                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={() => setSelectedBioCastaway(castaway)}
                          title="View Contestant Bio"
                        >
                          Bio
                        </Button>

                        <Button
                          variant={isSelected ? 'success' : 'outline-primary'}
                          size="sm"
                          disabled={isFormDisabled || isSelected}
                          onClick={() => handleDirectPick(castaway.id)}
                          style={{ minWidth: '130px' }}
                        >
                          {isSubmittingThis ? (
                            <Spinner animation="border" size="sm" />
                          ) : isSelected ? (
                            '✓ Selected Pick'
                          ) : (
                            'Pick to Eliminate'
                          )}
                        </Button>
                      </div>
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            </Card>
          )}

          <div className="text-center mt-4">
            <Link to="/" className="btn btn-secondary">Back to Home</Link>
          </div>
        </Card.Body>
      </Card>

      {/* Castaway Bio Modal */}
      <CastawayBioModal
        show={Boolean(selectedBioCastaway)}
        onHide={() => setSelectedBioCastaway(null)}
        castaway={selectedBioCastaway}
        tribes={tribes}
      />
    </Container>
  );
};

export default EliminationPick;
